import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { PageHeader } from "../../components/common";
import { PermEditor, ACTION_LABEL, SCOPE_LABEL } from "../../components/PermEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const LV = { edit: "text-emerald-400", view: "text-sky-400", mask: "text-amber-400", none: "text-muted-foreground" };
const inp = "w-full bg-muted/60 border h-9 px-2 text-sm";

const RoleCell = ({ r }) => (
  <td className="min-w-[150px] max-w-[170px] sm:min-w-[230px] sm:max-w-[260px]">
    <div className="font-medium flex items-center gap-1.5 flex-wrap">{r.name}{r.custom && <span className="text-[10px] border px-1 text-muted-foreground" data-testid={`role-custom-${r.key}`}>added</span>}</div>
    <div className="text-[11px] text-muted-foreground whitespace-normal">{r.description}</div>
    {r.users !== undefined && <div className="text-[11px] text-muted-foreground" data-testid={`role-users-${r.key}`}>{r.users} login{r.users === 1 ? "" : "s"}</div>}
  </td>
);

export const RoleMatrix = ({ roles, catalog, onEdit, canEdit = () => true }) => (
  <div className="ax-panel ax-scroll ax-freeze" data-testid="role-matrix">
    <table className="ax-table w-full min-w-[1100px]">
      <thead><tr><th>Role</th>{catalog.modules.map((m) => <th key={m}>{m}</th>)}{Object.keys(catalog.sensitive_fields).map((f) => <th key={f} className="!text-[9px]">{f}</th>)}<th /></tr></thead>
      <tbody>{roles.map((r) => (
        <tr key={r.key} data-testid={`role-row-${r.key}`}>
          <RoleCell r={r} />
          {catalog.modules.map((m) => {
            const v = r.modules?.[m];
            return <td key={m} className="text-[11px]">{v?.actions?.length ? <><div className="text-primary">{SCOPE_LABEL[v.scope] || v.scope}</div><div className="text-muted-foreground">{v.actions.length === catalog.actions.length ? "all rights" : v.actions.map((a) => ACTION_LABEL[a] || a).join(", ")}</div></> : <span className="text-muted-foreground">no access</span>}</td>;
          })}
          {Object.keys(catalog.sensitive_fields).map((f) => <td key={f} className={`text-[11px] ${LV[r.fields?.[f] || "none"]}`}>{r.fields?.[f] || "none"}</td>)}
          <td>{onEdit && canEdit(r) && <button data-testid={`role-edit-${r.key}`} onClick={() => onEdit(r)} className="border text-xs px-2 py-1 hover:border-primary">Edit</button>}</td>
        </tr>
      ))}</tbody>
    </table>
  </div>
);

const DeleteRole = ({ role, onDone }) => {
  const del = () => {
    if (!window.confirm(`Delete the role "${role.name}"? This can't be undone.`)) return;
    api.delete(`/admin/roles/${role.key}`).then(() => { toast.success("Role deleted"); onDone(); }).catch((e) => toast.error(errMsg(e)));
  };
  return (
    <div className="mr-auto flex items-center gap-2">
      <button data-testid="role-delete" onClick={del} disabled={role.users > 0} className="border border-rose-400/60 text-rose-600 h-9 px-3 text-sm flex items-center gap-1.5 disabled:opacity-50"><Trash2 className="h-4 w-4" />Delete role</button>
      {role.users > 0 && <span className="text-xs text-muted-foreground" data-testid="role-delete-blocked">{role.users} login{role.users === 1 ? "" : "s"} still use it. Move them to another role first.</span>}
    </div>
  );
};

const RoleDialog = ({ role, catalog, founder, onClose, onSaved }) => {
  const [f, setF] = useState({ modules: {}, fields: {}, ...role });
  const isNew = !role.key;
  const locked = role.key === "founder";
  const save = () => (isNew
    ? api.post("/admin/roles", { name: f.name, description: f.description, modules: f.modules, fields: f.fields })
    : api.put(`/admin/roles/${role.key}`, { name: f.name, description: f.description, ...(locked ? {} : { modules: f.modules, fields: f.fields }) }))
    .then(() => { toast.success(isNew ? "Role added" : "Role saved. It applies on each user's next request."); onSaved(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-card" data-testid="role-dialog">
        <DialogHeader><DialogTitle className="font-display">{isNew ? "Add a role" : `Edit role · ${role.name}`}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs"><span className="ax-label">Role name</span><input data-testid="role-name" maxLength={60} className={inp} value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
          <label className="text-xs"><span className="ax-label">What this role does</span><input data-testid="role-description" maxLength={300} className={inp} value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} /></label>
        </div>
        {isNew && <p className="text-xs text-muted-foreground">A new role starts with no rights. Tick only the rights it needs.</p>}
        {!founder && !locked && <p className="text-xs text-amber-700" data-testid="role-founder-note">Only founders can give rights to Sales, UIDs, Operations, Business Plan, Dashboards, Employee Records, Manage Access or sensitive fields.</p>}
        {locked ? <p className="text-sm text-muted-foreground">The Founder role always has every right. Only its name and description can change.</p>
          : <PermEditor catalog={catalog} value={f} onChange={(v) => setF({ ...f, ...v })} testid="role-perm" />}
        <div className="flex flex-wrap gap-2 justify-end">
          {role.custom && <DeleteRole role={role} onDone={onSaved} />}
          <button data-testid="role-cancel" onClick={onClose} className="border h-9 px-4 text-sm">Cancel</button>
          <button data-testid="role-save" onClick={save} disabled={!(f.name || "").trim()} className="bg-primary text-primary-foreground h-9 px-4 text-sm disabled:opacity-50">{isNew ? "Add role" : "Save role"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminRoles() {
  const { can } = useAuth();
  const manage = can("admin", "manage_users");
  const founder = can("admin", "manage_access");
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/roles").then((r) => setRoles(r.data)), []);
  useEffect(() => { load(); api.get("/admin/catalog").then((r) => setCatalog(r.data)); }, [load]);
  if (!catalog) return null;
  return (
    <div>
      <PageHeader eyebrow="Administration" title="Role templates" desc="Starting permissions for each job role. Rename a role, add a new one (it starts with no rights) or delete a role you added once no one uses it. Override a role for one user under Users & access. The System Administrator role has no access to commercial data.">
        {manage && <button data-testid="add-role-button" onClick={() => setEdit({ name: "", description: "" })} className="bg-primary text-primary-foreground h-9 px-4 text-sm flex items-center gap-2"><Plus className="h-4 w-4" /> Add role</button>}
      </PageHeader>
      <RoleMatrix roles={roles} catalog={catalog} onEdit={manage ? setEdit : null} canEdit={(r) => r.key !== "founder" || founder} />
      {edit && <RoleDialog role={edit} catalog={catalog} founder={founder} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
